// "May the signed-in employee ACT in this area?", as one hook.
//
// Every grant carries a level (migration 0120), so a page has two questions to
// ask and they have different answers: `<AreaGate>` decides whether the page
// opens at all (level-blind — a read-only holder is entitled to read it), and
// this decides whether the page draws anything that writes.
//
// It reads the SAME cached my-areas query the sidebar and `<AreaGate>` use, so
// there is no second request and no second source of truth about the caller's
// grants. A page that fetched its own copy would be free to disagree with the
// sidebar about what someone may do.
//
// THIS IS MANNERS, NOT THE BOUNDARY. Every write route takes the `full` level
// server-side, so the same call is refused with a 403 whatever this returns.
// Hide the control because a button that always fails is a bad page — never
// because hiding it is what stops the write.
import { useQuery } from "@tanstack/react-query";

import { type Area, canWrite, hasArea } from "./areas";
import { myAreasQuery } from "./queries";

/** Does the caller hold ANY of these areas, at either level? The READ question,
 *  and the one a cross-area LINK asks: `<AreaGate>` opens a page to anyone
 *  holding one of the areas it lists, so a link into that page is followable
 *  under exactly the same condition. The Explorer, which five areas link at, has
 *  its own named spelling of this (`canOpenExplorer` / `useCanOpenExplorer`);
 *  use that rather than passing `EXPLORER_AREAS` here, so the rule stays in one
 *  place.
 *
 *  No `known` companion, deliberately. Every caller renders inside an
 *  `<AreaGate>`, which holds its children behind `<LoadingState>` until this same
 *  query resolves, so by the time a link is drawn the answer is already cached.
 *  A link is not a claim about anyone's access either way: the worst a false
 *  negative does is show a name as text. */
export function useHoldsAnyArea(areas: readonly Area[]): boolean {
  const granted = useQuery(myAreasQuery);
  return areas.some((area) => hasArea(granted.data ?? [], area));
}

/** Does the caller hold `area` at FULL access, and do we KNOW yet?
 *
 *  Two answers because a pending query means different things to a control and to
 *  a sentence. Hiding a button we cannot yet justify is right — it never flashes
 *  and then vanishes. But "your access is read only" is a CLAIM, and asserting it
 *  before `/permissions/me` lands tells a full-access operator something false on
 *  every cold load, until the copy silently swaps under them.
 *
 *  So: gate controls on `canWrite`, and gate any copy that describes the caller's
 *  access on `known && !canWrite`. */
export function useWriteAccess(area: Area): { canWrite: boolean; known: boolean } {
  const areas = useQuery(myAreasQuery);
  return { canWrite: canWrite(areas.data ?? [], area), known: areas.isSuccess };
}

/** Does the caller hold `area` at FULL access? False while the query is still in
 *  flight, so a page never flashes a control that is about to disappear — which
 *  is the right default for a CONTROL and the wrong one for copy. See
 *  `useWriteAccess`. */
export function useCanWrite(area: Area): boolean {
  return useWriteAccess(area).canWrite;
}
